import { useCallback, useState } from "react"
import type { CreateFeatureDto, Feature, PluginspaceFeature, UpdateFeatureDto, ResponseCreateUpdate } from "../types"
import { DELETE, GET_ADM, POST_ADM, PUT_ADM    } from "../../../../services/api";
export function useFeaturesPluginsSpace() {
    const [features, setFeatures] = useState<Feature[]>([])

    const resolveListData = useCallback(<T,>(response: any): T[] => {
        if (Array.isArray(response)) {
            return response as T[]
        }

        if (Array.isArray(response?.data)) {
            return response.data as T[]
        }

        return []
    }, [])

    const hasRequestFailed = useCallback((response: any): boolean => {
        return response?.success === false
    }, [])

    const getAllFeatures = useCallback(async (): Promise<Feature[]> => {
        try {
            const response = await GET_ADM(`/features`)
            if (hasRequestFailed(response)) {
                setFeatures([])
                return []
            }

            const allFeatures = resolveListData<Feature>(response)
            setFeatures(allFeatures)
            return allFeatures
        } catch (error) {
            setFeatures([])
            return []
        }
    }, [hasRequestFailed, resolveListData])

    const createFeature = useCallback(async (feature: CreateFeatureDto): Promise<ResponseCreateUpdate | null> => {
        try {
            const response = await POST_ADM(`/features`, feature)
            if (!response.success) {
                return response
            }
            const newFeature = response.data
            setFeatures(prevFeatures => [...prevFeatures, newFeature])
            return response
        } catch (error) {
            return null
        }
    }, [])

    const updateFeature = useCallback(async (id: number, feature: UpdateFeatureDto): Promise<ResponseCreateUpdate | null> => {
        try {
            const response = await PUT_ADM(`/features/${id}`, feature)
            if (!response.success) {
                return response
            }
            const updatedFeature = response.data
            setFeatures(prevFeatures => prevFeatures.map(f => f.id === id ? updatedFeature : f))
            return response
        } catch (error) {
            return null
        }
    }, [])

    const deleteFeature = useCallback(async (id: number): Promise<boolean> => {
        try {
            const response = await DELETE(`/features/${id}`)
            if (!response?.success) {
                return false
            }

            setFeatures((prevFeatures) => prevFeatures.filter((feature) => feature.id !== id))
            return true
        } catch (error) {
            return false
        }
    }, [])


    const getAllFeaturesOfPluginspace = useCallback(async (pluginspaceId: number): Promise<PluginspaceFeature[]> => {
        const endpoints = [`/pluginspace-features/pluginspace/${pluginspaceId}`]

        for (const endpoint of endpoints) {
            try {
                const response = await GET_ADM(endpoint)
                if (hasRequestFailed(response)) {
                    continue
                }

                return resolveListData<PluginspaceFeature>(response)
            } catch (error) {
                // tenta próximo endpoint
            }
        }

        return []
    }, [hasRequestFailed, resolveListData])

    const createFeatureForPluginspace = useCallback(async (pluginspaceId: number, featureId: number): Promise<boolean> => {
        const endpoints = [`/pluginspace-features`]

        for (const endpoint of endpoints) {
            try {
                const response = await POST_ADM(endpoint, { pluginspaceId, featureId })
                if (response?.success) {
                    return true
                }
            } catch (error) {
                // tenta próximo endpoint
            }
        }

        return false
    }, [])

    const removeFeatureForPluginspace = useCallback(async (pluginspaceId: number, featureId: number): Promise<boolean> => {
        const endpoints = [`/pluginspace-features/${pluginspaceId}/${featureId}`]

        for (const endpoint of endpoints) {
            try {
                const response = await DELETE(endpoint)
                if (response?.success) {
                    return true
                }
            } catch (error) {
                // tenta próximo endpoint
            }
        }

        return false
    }, [])

    return {
        getAllFeatures,
        getAllFeaturesOfPluginspace,
        createFeatureForPluginspace,
        removeFeatureForPluginspace,
        features,
        updateFeature,
        deleteFeature,
        createFeature
    }
}